import { Injectable } from '@angular/core';
import { ENROLL_EVENT_REPORT } from 'src/app/@constants/local-storage-key-constants';
import { getLocalStorageItem } from 'src/app/@utils/local-storage-util';

interface EnrollEventReport {
  eventNameIdentifier: string;
  email: string;
  sendEventReportNotification: boolean;
}

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  constructor() {
    this._initializeUserFromStorage();
    this._startSession();
    window.addEventListener('beforeunload', () => this._trackSessionEnd());
  }
  private _userId: string | null = null;
  private _userEmailDomain: string | null = null;
  private _sessionStartTime: number | null = null;
  private _enrollmentData: EnrollEventReport | null = null;

  setUserId(email: string): void {
    this._userId = btoa(email);
    this._userEmailDomain = email.split('@')[1];

    window.gtag('config', 'G-X98B15C2YG', {
      ...this._getUserParams(),
      send_page_view: true,
      cookie_flags: 'max-age=7200;secure;samesite=none',
    });

    window.gtag('set', {
      ...this._getUserParams(),
      first_visit_timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
    });
  }

  trackPageView(pagePath: string, pageTitle: string): void {
    window.gtag('event', 'page_view', {
      ...this._getUserParams(),
      page_path: pagePath,
      page_title: pageTitle,
      page_referrer: document.referrer,
      timestamp: new Date().toISOString(),
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      engagement_time_msec: this._getTimeOnPage(),
    });
  }

  trackEvent(eventName: string, eventParams: Record<string, any> = {}): void {
    window.gtag('event', eventName, {
      ...this._getUserParams(),
      ...eventParams,
      timestamp: new Date().toISOString(),
      page_location: window.location.href,
      page_title: document.title,
    });
  }

  trackEngagement(engagementTime: number, engagementType: string): void {
    window.gtag('event', 'user_engagement', {
      ...this._getUserParams(),
      engagement_time_msec: engagementTime * 1000,
      engagement_type: engagementType,
      timestamp: new Date().toISOString(),
      page_location: window.location.href,
      page_title: document.title,
    });
  }

  trackScrollDepth(scrollDepth: number): void {
    window.gtag('event', 'scroll', {
      ...this._getUserParams(),
      scroll_depth: scrollDepth,
      page_location: window.location.href,
      timestamp: new Date().toISOString(),
    });
  }

  trackDownload(fileName: string, fileType: string): void {
    window.gtag('event', 'file_download', {
      ...this._getUserParams(),
      file_name: fileName,
      file_type: fileType,
      timestamp: new Date().toISOString(),
    });
  }

  trackFormInteraction(
    formId: string,
    action: 'start' | 'complete' | 'error',
    details?: any
  ): void {
    window.gtag('event', 'form_' + action, {
      ...this._getUserParams(),
      form_id: formId,
      form_name: formId,
      ...details,
      timestamp: new Date().toISOString(),
    });
  }

  trackClick(
    elementId: string,
    elementType: string,
    elementContent?: string
  ): void {
    window.gtag('event', 'click', {
      ...this._getUserParams(),
      element_id: elementId,
      element_type: elementType,
      element_content: elementContent,
      page_location: window.location.href,
      timestamp: new Date().toISOString(),
    });
  }

  trackError(
    errorType: string,
    errorMessage: string,
    errorStack?: string
  ): void {
    window.gtag('event', 'error', {
      ...this._getUserParams(),
      error_type: errorType,
      error_message: errorMessage,
      error_stack: errorStack,
      page_location: window.location.href,
      timestamp: new Date().toISOString(),
    });
  }

  trackSearch(searchTerm: string, resultCount: number): void {
    window.gtag('event', 'search', {
      ...this._getUserParams(),
      search_term: searchTerm,
      result_count: resultCount,
      timestamp: new Date().toISOString(),
    });
  }

  trackVideoInteraction(
    videoId: string,
    action: 'play' | 'pause' | 'complete',
    currentTime: number
  ): void {
    window.gtag('event', 'video_' + action, {
      ...this._getUserParams(),
      video_id: videoId,
      current_time: currentTime,
      timestamp: new Date().toISOString(),
    });
  }

  private _getUserParams(): Record<string, any> {
    return {
      user_id: this._userId ?? 'anonymous',
      user_email_domain: this._userEmailDomain ?? 'unknown',
      has_email: this._userId ? 'true' : 'false',
      event_name: this._enrollmentData?.eventNameIdentifier ?? 'unknown',
      wants_notifications:
        this._enrollmentData?.sendEventReportNotification ?? false,
    };
  }

  private _initializeUserFromStorage(): void {
    const storedData =
      getLocalStorageItem<EnrollEventReport>(ENROLL_EVENT_REPORT);
    if (storedData?.email) {
      this._enrollmentData = storedData;
      this.setUserId(storedData.email);
      this.trackEvent('user_restored_from_storage', {
        event_name: storedData.eventNameIdentifier,
        wants_notifications: storedData.sendEventReportNotification,
      });
    }
  }

  private _startSession(): void {
    this._sessionStartTime = Date.now();
    window.gtag('event', 'session_start', {
      ...this._getUserParams(),
      timestamp: new Date().toISOString(),
      entry_point: window.location.href,
      referrer: document.referrer,
      is_returning_user: !!this._userId,
    });
  }

  private _trackSessionEnd(): void {
    if (this._sessionStartTime) {
      const sessionDuration = (Date.now() - this._sessionStartTime) / 1000;
      window.gtag('event', 'session_end', {
        ...this._getUserParams(),
        session_duration: sessionDuration,
        exit_page: window.location.href,
        timestamp: new Date().toISOString(),
        is_returning_user: !!this._userId,
      });
    }
  }

  private _getTimeOnPage(): number {
    return this._sessionStartTime ? Date.now() - this._sessionStartTime : 0;
  }
}
